import { Link } from "react-router-dom";
import { 
  Shield, 
  ArrowLeft, 
  Code, 
  Terminal, 
  Key, 
  Server,
  FileJson,
  Lock,
  CheckCircle,
  Copy
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";

export default function ApiDocsPage() {
  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    toast.success("Copied to clipboard");
  };

  const endpoints = [
    {
      method: "POST",
      path: "/functions/v1/receive-metrics",
      description: "Submit server performance metrics from the monitoring agent",
      auth: "Device Token + HMAC",
      badge: "Agent"
    },
    {
      method: "POST",
      path: "/functions/v1/receive-windows-logs",
      description: "Submit Windows event logs for centralized analysis",
      auth: "Device Token + HMAC",
      badge: "Agent"
    },
    {
      method: "POST",
      path: "/functions/v1/register-server",
      description: "Register a new server using an enrollment token",
      auth: "Enrollment Token",
      badge: "Agent"
    },
    {
      method: "POST",
      path: "/functions/v1/generate-enrollment-token",
      description: "Generate a new enrollment token for agent deployment",
      auth: "User JWT",
      badge: "Dashboard"
    },
    {
      method: "POST",
      path: "/functions/v1/security-alerts",
      description: "Trigger and manage security alerts",
      auth: "User JWT",
      badge: "Dashboard"
    }
  ];

  return (
    <div className="min-h-screen bg-[hsl(224,71%,4%)] text-white">
      {/* Header */}
      <header className="border-b border-white/10 sticky top-0 z-50 bg-[hsl(224,71%,4%)]/95 backdrop-blur-sm">
        <div className="container mx-auto px-4 py-4">
          <nav className="flex items-center justify-between">
            <Link to="/admin-access" className="flex items-center gap-3">
              <div className="p-2 bg-primary rounded-lg">
                <Shield className="w-6 h-6 text-primary-foreground" />
              </div>
              <span className="text-xl font-bold">ServSpect</span>
            </Link>
            <Button variant="ghost" asChild className="text-white hover:bg-white/10">
              <Link to="/admin-access">
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back to Home
              </Link>
            </Button>
          </nav>
        </div>
      </header>

      {/* Hero Section */}
      <section className="py-16 bg-gradient-to-b from-primary/10 to-transparent">
        <div className="container mx-auto px-4 text-center">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary/20 border border-primary/30 mb-6">
            <Code className="w-5 h-5 text-primary" />
            <span className="text-primary font-medium">Developer Resources</span>
          </div>
          <h1 className="text-4xl md:text-5xl font-bold mb-6">
            API Documentation
          </h1>
          <p className="text-xl text-white/70 max-w-3xl mx-auto">
            Integrate ServSpect into your infrastructure with our secure REST API. 
            All endpoints are protected with device token authentication and HMAC signatures.
          </p>
        </div>
      </section>

      {/* Quick Start */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl font-bold mb-8">Quick Start</h2>
          
          <div className="grid lg:grid-cols-3 gap-6 mb-12">
            <Card className="bg-white/5 border-white/10">
              <CardHeader>
                <div className="w-10 h-10 rounded-lg bg-primary/20 flex items-center justify-center mb-2">
                  <Key className="w-5 h-5 text-primary" />
                </div>
                <CardTitle className="text-white">1. Get Enrollment Token</CardTitle>
              </CardHeader>
              <CardContent>
                <CardDescription className="text-white/60">
                  Generate an enrollment token from the dashboard. This token is used to register 
                  new servers and generate device tokens.
                </CardDescription>
              </CardContent>
            </Card>
            
            <Card className="bg-white/5 border-white/10">
              <CardHeader>
                <div className="w-10 h-10 rounded-lg bg-primary/20 flex items-center justify-center mb-2">
                  <Server className="w-5 h-5 text-primary" />
                </div>
                <CardTitle className="text-white">2. Register Server</CardTitle>
              </CardHeader>
              <CardContent>
                <CardDescription className="text-white/60">
                  Use the enrollment token to register your server. You'll receive a device token 
                  for secure API authentication.
                </CardDescription>
              </CardContent>
            </Card>
            
            <Card className="bg-white/5 border-white/10">
              <CardHeader>
                <div className="w-10 h-10 rounded-lg bg-primary/20 flex items-center justify-center mb-2">
                  <Lock className="w-5 h-5 text-primary" />
                </div>
                <CardTitle className="text-white">3. Send Metrics</CardTitle>
              </CardHeader>
              <CardContent>
                <CardDescription className="text-white/60">
                  Sign your payloads with HMAC-SHA256 using the device token and submit 
                  metrics to the API.
                </CardDescription>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Authentication */}
      <section className="py-16 bg-[hsl(224,71%,6%)]">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl font-bold mb-8">Authentication</h2>
          
          <div className="grid lg:grid-cols-2 gap-8">
            <div>
              <h3 className="text-xl font-semibold mb-4">Required Headers</h3>
              <p className="text-white/70 mb-6">
                All agent API calls require the following headers for authentication:
              </p>
              
              <div className="space-y-4">
                <div className="p-4 bg-white/5 border border-white/10 rounded-lg">
                  <div className="flex items-center justify-between mb-2">
                    <code className="text-primary">x-device-id</code>
                    <Badge variant="outline" className="border-primary/50 text-primary">Required</Badge>
                  </div>
                  <p className="text-white/60 text-sm">Unique identifier for the registered device</p>
                </div>
                
                <div className="p-4 bg-white/5 border border-white/10 rounded-lg">
                  <div className="flex items-center justify-between mb-2">
                    <code className="text-primary">x-device-token</code>
                    <Badge variant="outline" className="border-primary/50 text-primary">Required</Badge>
                  </div>
                  <p className="text-white/60 text-sm">Device token received during registration</p>
                </div>
                
                <div className="p-4 bg-white/5 border border-white/10 rounded-lg">
                  <div className="flex items-center justify-between mb-2">
                    <code className="text-primary">x-signature</code>
                    <Badge variant="outline" className="border-primary/50 text-primary">Required</Badge>
                  </div>
                  <p className="text-white/60 text-sm">HMAC-SHA256 signature of the request body</p>
                </div>
              </div>
            </div>
            
            <div>
              <h3 className="text-xl font-semibold mb-4">Example Request</h3>
              <div className="relative">
                <pre className="p-4 bg-black/50 border border-white/10 rounded-lg overflow-x-auto text-sm">
                  <code className="text-white/80">{`POST /functions/v1/receive-metrics
Host: api.servspect.com
Content-Type: application/json
x-device-id: your-device-id
x-device-token: your-device-token
x-signature: hmac-sha256-signature

{
  "hostname": "server-01",
  "timestamp": "2024-01-15T10:30:00Z",
  "cpu_usage": 45.2,
  "memory_usage": 62.8,
  "disk_usage": 34.5,
  "network_in_mbps": 12.4,
  "network_out_mbps": 8.7
}`}</code>
                </pre>
                <Button 
                  variant="ghost" 
                  size="sm" 
                  className="absolute top-2 right-2 text-white/60 hover:text-white"
                  onClick={() => copyToClipboard(`POST /functions/v1/receive-metrics`)}
                >
                  <Copy className="w-4 h-4" />
                </Button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Endpoints */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl font-bold mb-8">API Endpoints</h2>
          
          <div className="space-y-4">
            {endpoints.map((endpoint, index) => (
              <div key={index} className="p-6 bg-white/5 border border-white/10 rounded-lg">
                <div className="flex flex-wrap items-center gap-3 mb-3">
                  <Badge 
                    className={`${
                      endpoint.method === 'POST' ? 'bg-green-500/20 text-green-400 border-green-500/30' :
                      endpoint.method === 'GET' ? 'bg-blue-500/20 text-blue-400 border-blue-500/30' :
                      'bg-yellow-500/20 text-yellow-400 border-yellow-500/30'
                    }`}
                  >
                    {endpoint.method}
                  </Badge>
                  <code className="text-primary font-mono">{endpoint.path}</code>
                  <Badge variant="outline" className="border-white/20 text-white/60 ml-auto">
                    {endpoint.badge}
                  </Badge>
                </div>
                <p className="text-white/70 mb-3">{endpoint.description}</p>
                <div className="flex items-center gap-2 text-sm text-white/50">
                  <Lock className="w-4 h-4" />
                  <span>Auth: {endpoint.auth}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Rate Limits */}
      <section className="py-16 bg-[hsl(224,71%,6%)]">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl font-bold mb-8">Rate Limits</h2>
          
          <div className="grid md:grid-cols-2 gap-6 max-w-3xl">
            <div className="p-6 bg-white/5 border border-white/10 rounded-lg">
              <div className="flex items-center gap-3 mb-4">
                <FileJson className="w-6 h-6 text-primary" />
                <span className="font-semibold">Metrics Endpoint</span>
              </div>
              <p className="text-3xl font-bold text-primary mb-2">100</p>
              <p className="text-white/60">requests per device per hour</p>
            </div>
            
            <div className="p-6 bg-white/5 border border-white/10 rounded-lg">
              <div className="flex items-center gap-3 mb-4">
                <Terminal className="w-6 h-6 text-primary" />
                <span className="font-semibold">Event Logs Endpoint</span>
              </div>
              <p className="text-3xl font-bold text-primary mb-2">30</p>
              <p className="text-white/60">requests per device per hour</p>
            </div>
          </div>
          
          <div className="mt-8 p-4 bg-primary/10 border border-primary/30 rounded-lg max-w-3xl">
            <div className="flex items-start gap-3">
              <CheckCircle className="w-5 h-5 text-primary mt-0.5" />
              <div>
                <p className="font-medium mb-1">Need higher limits?</p>
                <p className="text-white/60 text-sm">
                  Enterprise plans include custom rate limits based on your monitoring needs. 
                  Contact us for more information.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-white/10 py-8 bg-[hsl(224,71%,4%)]">
        <div className="container mx-auto px-4 text-center text-white/60 text-sm">
          <p>© {new Date().getFullYear()} ServSpect. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
}
